<script lang="ts" setup>
	import { createReusableTemplate } from '@vueuse/core'
	import StringMethods from '~/utils/character'
	import { NAVLINKS } from '~/utils/constants'
	const { diasporaProducts, productFilterKey } = storeToRefs(useProductsStore())
	const { openPricing } = useProductsStore()
	const { toggleWaitlistModal } = useStore()
	const props = defineProps<{
		variant: string
	}>()
	const { toLower } = StringMethods()
	const viewKey = computed(() => toLower(props.variant || 'sendcare') as 'sendcare' | NAVLINKS.sendcareWaitlist)
	const menuOption = reactive({
		key: '',
		open: false
	})
	const navLinks: Array<{
		label: string
		callback: (arg: string) => void
		dropdownItems?: {
			label: string
			key: string
			description: string
		}[]
	}> = [
		{
			label: 'Hospital list',
			callback: () => openPricing('hospital')
		},
		{
			label: 'Plans',
			callback: (key: string) => {
				menuOption.open = !menuOption.open
				menuOption.key = menuOption.key === key ? '' : key
			},
			dropdownItems: [
				{
					label: 'Senior Plans',
					key: 'senior-plans',
					description: 'Suitable for adult and parents'
				},
				{
					label: 'Regular Plans',
					key: 'regular-plans',
					description: 'Suitable for anyone'
				}
			]
		},
		{
			label: 'How it works',
			callback: () => openPricing('how_it_works')
		}
	]
	const selectPlan = (e: string) => {
		navigateTo({
			hash: '#pricing'
		})
		productFilterKey.value = e
		menuOption.open = false
		isMenuOpen.value = false
	}
	const isMenuOpen = shallowRef(false)
	const [defineLoginList, useLoginList] = createReusableTemplate<{ btnCls?: string }>()
	const [defineDropdown, dropdownTemplate] = createReusableTemplate<{ isOpen: boolean }>({
		props: {
			isOpen: {
				default: false,
				type: Boolean
			}
		}
	})
	const { isMobile } = useMobile()
	const openLink = (e: 'login' | 'get-started') => {
		const link =
			e === 'login'
				? `${import.meta.env.VITE_INDIVIDUAL_URL}/login`
				: `${import.meta.env.VITE_INDIVIDUAL_URL}/sendcare/`
		navigateTo(link, {
			external: true,
			open: {
				target: '_blank'
			}
		})
	}
</script>
<template>
	<header class="w-full sticky top-0 z-50 left-0 bg-[#fff1ea]">
		<define-login-list v-slot="{ btnCls }">
			<div class="lg:gap-2 items-stretch gap-[12px] w-full lg:w-auto justify-between">
				<ui-sendcare-btn text="Login" type="outline" :class="btnCls || ''" @trigger="openLink('login')" />
				<ui-sendcare-btn
					text="Get started"
					type="primary"
					:class="btnCls || ''"
					@trigger="openLink('get-started')"
				/>
			</div>
		</define-login-list>

		<define-dropdown v-slot="{ isOpen }">
			<div
				:class="[
					isOpen ? 'lg:h-[280px] max-h-[330px] pb-[80px]' : 'h-0 max-h-0',
					'sub-menu w-full  bg-[#fffefe] backdrop-blur-md custom-transition-2 overflow-hidden overflow-y-auto'
				]"
			>
				<sendcare-wrapper
					class-name="mx-auto w-full md:px-[auto] px-[16px] lg:px-14 lg:space-y-[23px]  lg:py-[36px]"
				>
					<sendcare-nav-links :product-list="diasporaProducts" @selected="selectPlan" />
				</sendcare-wrapper>
			</div>
		</define-dropdown>

		<div
			class="h-[90px] inline-flex items-center gap-4 w-full lg:overflow-hidden bg-[#fff1ea] md:bg-[#fffefe] border-b border-[#ffe2d3]"
		>
			<sendcare-wrapper class-name="w-full">
				<nav class="mx-auto w-full flex items-center justify-between">
					<div class="inline-flex justify-between items-center lg:gap-x-[102px]">
						<tools-icon
							dir="internal-logo"
							icon="sendcare"
							svg-class="md:w-[173px] md:h-[34.31px] h-[28.72px] w-[145px]"
							class="flex-none"
						/>
						<ul
							v-if="viewKey === 'sendcare'"
							class="lg:inline-flex lg:static gap-4 lg:py-0 py-[40px] space-y-[18px] lg:space-y-0 items-center fixed top-[90px] left-0 z-50 bg-white lg:bg-transparent h-dvh lg:h-auto lg:w-auto overflow-hidden custom-transition-2"
							:class="{ 'sm:w-[50%] w-full': isMenuOpen, 'w-[0]': !isMenuOpen }"
						>
							<li
								v-for="link in navLinks"
								:key="link.label"
								class="font-semibold text-base sm:whitespace-nowrap text-[#3C3C3C] md:hover:text-[#FE7D35] transition-colors ease-linear flex-none w-full lg:w-auto"
							>
								<div class="w-full">
									<button
										type="button"
										class="lg:px-[18px] lg:py-[16px] py-[16px] px-[16px] lg:justify-between rounded-[4px] flex items-center gap-1 w-full lg:w-auto"
										:class="{
											'text-[#FE7D35]': link.label === menuOption.key && menuOption.open
										}"
										:aria-expanded="menuOption.open"
										@click="link.callback(link.label)"
									>
										{{ link.label }}
										<tools-icon
											v-if="link?.dropdownItems"
											icon="caret-down"
											dir="arrows"
											:width="12"
											path-class="stroke-[#3C3C3C]"
											:class="[
												menuOption.open ? '-rotate-180' : 'rotate-0',
												'custom-transition-2'
											]"
										/>
									</button>
								</div>

								<dropdown-template v-if="isMobile" :is-open="menuOption.key === link.label" />
							</li>
							<li class="lg:hidden px-[16px] pt-[34px] w-full">
								<use-login-list class="inline-flex sm:hidden flex-wrap" btn-cls="flex-1" />
							</li>
						</ul>
					</div>
					<div class="inline-flex items-center gap-5">
						<template v-if="viewKey === 'sendcare'">
							<use-login-list class="hidden sm:inline-flex" btn-cls="flex-none" />
							<ui-hamburgger
								v-model:show="isMenuOpen"
								aria-label="menu"
								class="h-[34px] w-[34px] md:h-[40px] md:w-[40px] justify-center rounded-[2px] flex items-center gap-1 bg-white flex-none lg:hidden"
							/>
						</template>
						<template v-else-if="viewKey === 'sendcare-waitlist'">
							<ui-sendcare-btn text="Join the Waitlist" type="primary" @trigger="toggleWaitlistModal" />
						</template>
					</div>
				</nav>
			</sendcare-wrapper>
		</div>

		<dropdown-template v-if="!isMobile" :is-open="menuOption.open" />
	</header>
</template>

<style></style>
